import { useState } from "react";
import { t } from "../../i18n";
import { SeoLocale, SeoMetadata } from "../../http/protocol";
import { SeoCountedField } from "./SeoCountedField";
import { SeoFieldFallback } from "./SeoFieldFallback";
import { hasOgImageError } from "./seoEditorUtils";
import { isFilled, resolveFallback, toWebUrl } from "./seoFallback";
import { SeoFallbackInputsType, SeoField } from "./seoTypes";
import { SERP_DESCRIPTION_LIMIT, SERP_TITLE_LIMIT, SerpPreview } from "./SerpPreview";

interface SeoLocalePanelProps {
  locale: SeoLocale;
  value: SeoMetadata;
  fallbackInputs: SeoFallbackInputsType;
  cmsAvailable?: boolean;
  displayUrl: string;
  onChange: (locale: string, field: SeoField, value: string) => void;
  testIdPrefix: string;
}

interface SeoOgImagePreviewProps {
  url: string;
  testId: string;
}

const FIELD_TEST_IDS: Record<SeoField, string> = {
  title: "title",
  description: "description",
  keywords: "keywords",
  ogImageUrl: "og-image-url",
};

function effectiveValue(field: SeoField, value: SeoMetadata, fallbackInputs: SeoFallbackInputsType): string {
  const own = value[field];
  if (isFilled(own)) {
    return own;
  }
  return resolveFallback(field, fallbackInputs)?.value ?? "";
}

function SeoOgImagePreview({ url, testId }: SeoOgImagePreviewProps): JSX.Element | null {
  // A broken URL would otherwise leave the browser's broken-image icon; the host remounts this per URL.
  const [failed, setFailed] = useState(false);
  if (failed) {
    return null;
  }
  return (
    <img
      className="seo-og-preview img-thumbnail mt-2"
      src={url}
      alt={t("admin.seo.og-image-preview", undefined, "Social share image preview")}
      data-testid={testId}
      onError={() => setFailed(true)}
    />
  );
}

/** One locale's fields, with the search result preview beside them. */
export function SeoLocalePanel({ locale, value, fallbackInputs, cmsAvailable, displayUrl, onChange, testIdPrefix }: SeoLocalePanelProps): JSX.Element {
  const code = locale.code ?? "";
  const rowTestId = `${testIdPrefix}-${code}`;
  const ogImageUrl = value.ogImageUrl ?? "";
  const ogImageWebUrl = toWebUrl(ogImageUrl);
  const ogImageInvalid = hasOgImageError(value);

  function fallbackFor(field: SeoField): JSX.Element | undefined {
    if (isFilled(value[field])) {
      return undefined;
    }
    return (
      <SeoFieldFallback
        field={field}
        fallbackInputs={fallbackInputs}
        cmsAvailable={cmsAvailable}
        testId={`${rowTestId}-${FIELD_TEST_IDS[field]}-fallback`}
      />
    );
  }

  return (
    <div data-testid={`${rowTestId}-row`}>
      <div className="row">
        <div className="col-lg-7">
          <SeoCountedField
            id={`${rowTestId}-title`}
            label={t("admin.seo.title", undefined, "SEO title")}
            value={value.title ?? ""}
            limit={SERP_TITLE_LIMIT}
            multiline={false}
            onChange={(title) => onChange(code, "title", title)}
            fallback={fallbackFor("title")}
          />
          <SeoCountedField
            id={`${rowTestId}-description`}
            label={t("admin.seo.description", undefined, "Meta description")}
            value={value.description ?? ""}
            limit={SERP_DESCRIPTION_LIMIT}
            multiline={true}
            onChange={(description) => onChange(code, "description", description)}
            fallback={fallbackFor("description")}
          />
          <div className="form-group">
            <label htmlFor={`${rowTestId}-keywords`}>{t("admin.seo.keywords", undefined, "Meta keywords")}</label>
            <input
              id={`${rowTestId}-keywords`}
              className="form-control"
              type="text"
              value={value.keywords ?? ""}
              data-testid={`${rowTestId}-keywords`}
              onChange={(event) => onChange(code, "keywords", event.target.value)}
            />
            {fallbackFor("keywords")}
          </div>
          <div className="form-group">
            <label htmlFor={`${rowTestId}-og-image-url`}>{t("admin.seo.og-image-url", undefined, "Social share image URL (OG image)")}</label>
            <input
              id={`${rowTestId}-og-image-url`}
              className={ogImageInvalid ? "form-control is-invalid" : "form-control"}
              type="url"
              value={ogImageUrl}
              data-testid={`${rowTestId}-og-image-url`}
              onChange={(event) => onChange(code, "ogImageUrl", event.target.value)}
            />
            {ogImageInvalid && (
              <div
                className="invalid-feedback"
                data-testid={`${rowTestId}-og-image-url-error`}
              >
                {t("admin.seo.og-image-url-invalid", undefined, "Must be an http(s) address without spaces")}
              </div>
            )}
            {fallbackFor("ogImageUrl")}
            {ogImageWebUrl !== undefined && (
              <SeoOgImagePreview
                key={ogImageWebUrl}
                url={ogImageWebUrl}
                testId={`${rowTestId}-og-image-preview`}
              />
            )}
          </div>
        </div>
        <div className="col-lg-5">
          <SerpPreview
            displayUrl={displayUrl}
            title={effectiveValue("title", value, fallbackInputs)}
            description={effectiveValue("description", value, fallbackInputs)}
            testId={`${rowTestId}-serp-preview`}
          />
        </div>
      </div>
    </div>
  );
}
